import type { ReactElement } from 'react';

import styled, { css, useTheme } from 'styled-components';

import CloseIcon from '../../core/icons/CloseModalIcon';
import HamburgerIcon from '../../core/icons/HamburgerIcon';
import { media } from '../../core/styles';
import { useMobileTopLevelNavigationActive } from '../topLevelNavigation/hooks/useTopLevelNavigationEnabled';
import { MOBILE_TOP_LEVEL_NAVIGATION_ID } from '../topLevelNavigation/mobile/constants';
import { useNavActionContext } from '~/utilities/context/dynamic/NavActionContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';
import { useHydrated } from '~/utilities/hooks/useHydrated';
import { useTopNavTracking } from '~/utilities/hooks/useTopNavTracking';

// Declared before `S` so the icon parts can style themselves from its `data-opened` state.
const Button = styled.button`
  ${({ theme }) => css`
    border: 0;
    box-sizing: border-box;
    cursor: pointer;
    font-size: 0;
    height: var(--top-nav-height);
    width: ${theme.font.size['10']};
    padding: ${theme.size['4']};
    display: inline-block;
    position: relative;

    @media ${media(theme).greaterThan('lg')} {
      display: inline-block;
    }

    ${getSiteStyles(theme.siteType, {
      [SiteType.Aaf]: css`
        width: 54px;
        padding: ${theme.size['0']};
      `,
    })}
  `}
`;

const OPENED = `${Button}[data-opened='true']`;

const S = {
  HamburgerButton: Button,
  HamburgerIcon: styled.span`
    ${({ theme }) => css`
      position: absolute;
      display: inline-block;
      height: 14px;
      left: calc(50% - 9px);
      top: calc(50% - 7px);
      width: 18px;

      @media ${media(theme).greaterThan('sm')} {
        height: 18px;
        width: 18px;
        left: calc(50% - 12px);
        top: calc(50% - 9.5px);
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Gstar]: css`
          @media ${media(theme).greaterThan('sm')} {
            height: 14px;
            width: 18px;
            left: calc(50% - 9px);
            top: calc(50% - 7px);
          }
        `,
        [SiteType.Aaf]: css`
          @media ${media(theme).greaterThan('sm')} {
            top: calc(50% - 5px);
          }
        `,
      })}
    `}
  `,
  Stick: styled.span`
    ${({ theme }) => css`
      background-color: ${theme.colors.content.component.header.color};
      display: block;
      height: 2px;
      border-radius: 1px;
      margin: ${theme.padding['3xs']} ${theme.padding.none};
      transition: all 0.25s cubic-bezier(0.35, 0, 0.25, 1) 0.3s;
      width: 100%;
      opacity: 1;

      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }

      ${OPENED} &:first-child {
        transform: translate(0, 6px) rotate(45deg);
      }

      ${OPENED} &:nth-child(2) {
        opacity: 0;
      }

      ${OPENED} &:last-child {
        transform: translate(0, -6px) rotate(-45deg);
      }

      @media (prefers-reduced-motion) {
        transition: none;
      }
    `}
  `,
  CloseIconWrapper: styled.div`
    ${({ theme }) => css`
      display: none;

      ${OPENED} & {
        display: block;
      }

      @media ${media(theme).greaterThan('xl')} {
        &,
        ${OPENED} & {
          display: none;
        }
      }
    `}
  `,
  HamburgerIconWrapper: styled.div`
    ${({ theme }) => css`
      display: block;

      ${OPENED} & {
        display: none;
      }

      @media ${media(theme).greaterThan('xl')} {
        &,
        ${OPENED} & {
          display: block;
        }
      }
    `}
  `,
};

export const HamburgerButton = (): ReactElement => {
  const { sideNavOpened, toggleSideNav } = useNavActionContext();
  const { accessibilityLabels: { openSideBar, closenSideBar } = {} } = useStaticContext();
  const { isGStar } = useAppContext();
  const theme = useTheme();
  const { track } = useTopNavTracking();
  const mobileTopLevelNavigationActive = useMobileTopLevelNavigationActive();
  // Both depend on the viewport (and `sessionStorage` for the open state), which the server does
  // not know: until hydrated, render what the server rendered (closed, side navigation).
  const hydrated = useHydrated();
  const opened = hydrated && sideNavOpened;
  // Below `lg` with the mobile TLN on, the hamburger opens the drawer instead of `#sideNav`.
  const controls =
    hydrated && mobileTopLevelNavigationActive ? MOBILE_TOP_LEVEL_NAVIGATION_ID : 'sideNav';

  return (
    <S.HamburgerButton
      onClick={() => {
        track({
          event: 'header_interaction',
          element: 'nav_toggle',
          interaction_type: sideNavOpened ? 'close' : 'open',
        });
        toggleSideNav();
      }}
      data-testid="hamburger-menu"
      aria-label={opened ? closenSideBar : openSideBar}
      aria-controls={controls}
      aria-expanded={opened}
      data-opened={opened}
      id="hamburger-menu"
    >
      {isGStar && (
        <>
          <S.CloseIconWrapper>
            <CloseIcon color={theme.colors.content.component.header.color} />
          </S.CloseIconWrapper>
          <S.HamburgerIconWrapper>
            <HamburgerIcon color={theme.colors.content.component.header.color} />
          </S.HamburgerIconWrapper>
        </>
      )}
      {!isGStar && (
        <S.HamburgerIcon>
          <S.Stick />
          <S.Stick />
          <S.Stick />
        </S.HamburgerIcon>
      )}
    </S.HamburgerButton>
  );
};
